import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  getConsent,
  isConsentAnswered,
  acceptAllConsent,
  rejectNonEssentialConsent,
  ConsentStatus,
} from "../../lib/consent";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * CookieBanner Component
 *
 * The UI for recording a consent decision. Reading, writing, storage format,
 * and what each choice MEANS in terms of categories all live in
 * `lib/consent` — this component only renders the prompt and reflects the
 * answer. See docs/analytics/consent-foundation.md.
 */
const CookieBanner = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  // State for consent status: 'unknown' | 'accepted' | 'rejected'
  const [consentStatus, setConsentStatus] = useState(() => getConsent().status);

  // Visibility of the main banner
  const [isBannerVisible, setIsBannerVisible] = useState(() => !isConsentAnswered());

  // Visibility of the preferences modal
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  // Potential future state for detailed preferences
  const [preferences, setPreferences] = useState(() => getConsent().preferences);

  // Refs for accessibility (focus management)
  const openPrefsRef = useRef(null);
  const closePrefsRef = useRef(null);
  const bannerRef = useRef(null);

  // Reflect a decision that lib/consent has already recorded.
  const applyConsent = useCallback((consentData) => {
    setConsentStatus(consentData.status);
    setPreferences(consentData.preferences);

    // Once a choice is made, hide both
    setIsBannerVisible(false);
    setIsPreferencesOpen(false);
  }, []);

  // 3. Button Handlers
  const handleAcceptAll = (e) => {
    e?.preventDefault();
    applyConsent(acceptAllConsent());
  };

  const handleRefuse = (e) => {
    e?.preventDefault();
    applyConsent(rejectNonEssentialConsent());
  };

  const handleClosePreferences = (e) => {
    e?.preventDefault();
    setIsPreferencesOpen(false);
    // CRITICAL FIX: Closing the modal does NOT change isBannerVisible.
    // So if it was visible before, it remains visible now.
  };

  const handleDismissBanner = (e) => {
    e?.preventDefault();
    // Dismissing the banner without consent choice
    // Usually hides it for the current session/until refresh.
    setIsBannerVisible(false);
  };

  // 4. Accessibility: Escape key and Focus management
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (isPreferencesOpen) handleClosePreferences();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreferencesOpen]);

  // Focus management when modal opens/closes
  useEffect(() => {
    if (isPreferencesOpen) {
      closePrefsRef.current?.focus();
    } else if (consentStatus === ConsentStatus.UNKNOWN) {
      // Return focus to the trigger if we're still in "unknown" state
      openPrefsRef.current?.focus();
    }
  }, [isPreferencesOpen, consentStatus]);

  // Style objects for visibility
  // The banner is visible if we don't have consent AND the preferences modal is NOT open.

  // Effect to track preference changes (for future analytics/logic integration)
  useEffect(() => {
    if (import.meta.env?.DEV) {
      console.debug("Cookie preferences updated:", preferences);
    }
  }, [preferences]);

  return (
    <div className="cc">
      {/* Force visibility if consent is not yet given, overriding any engine/CSS hidden states */}
      <style>{`
        @keyframes cc-slide-up {
          0% { opacity: 0; transform: translateY(100%); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .cc-banner_component {
          display: ${isBannerVisible && !isPreferencesOpen ? "flex" : "none"} !important;
          opacity: 1 !important;
          visibility: visible !important;
          animation: ${isBannerVisible && !isPreferencesOpen ? "cc-slide-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards" : "none"} !important;
          transition: opacity 0.4s ease, transform 0.4s ease !important;
        }
      `}</style>

      {/* 
          Banner Component 
          Visible initially if no consent exists.
      */}
      <div
        ref={bannerRef}
        className="cc-banner_component"
        role="region"
        aria-label={t("cookies.banner.ariaLabel")}
      >
        <div className="cc-banner_container">
          <div className="cc-banner_text">
            <Trans
              i18nKey="cookies.banner.text"
              components={{
                1: <a href={localize("/privacy-policy")} className="cc-banner_text-link" />,
              }}
            />
          </div>
          <div className="cc-banner_buttons-wrapper">
            <a
              href="#"
              onClick={handleAcceptAll}
              className="cc-banner_button w-button"
              role="button"
            >
              {t("cookies.banner.acceptAll")}
            </a>
            <a
              href="#"
              onClick={handleRefuse}
              className="cc-banner_button cc-button-alt w-button"
              role="button"
            >
              {t("cookies.banner.refuse")}
            </a>
            <div
              onClick={handleDismissBanner}
              className="cc-banner_close"
              style={{ cursor: "pointer" }}
              role="button"
              aria-label={t("cookies.banner.dismissAriaLabel")}
            >
              <img
                src="/assets/photos/close-white.svg"
                loading="lazy"
                alt={t("cookies.banner.closeIconAlt")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
